import React, { useState, useMemo, useCallback } from 'react';
import { LanguageContext, useLanguage } from '@/components/LanguageState';
export { LanguageContext, useLanguage };

// English is loaded with English routes, not downloaded by every Danish visitor.
import flattenTranslations from '@/components/translations/flattenTranslations';
import daCommon from './translations/da/common';
import daHome from './translations/da/Home';
import daElaftale from './translations/da/Elaftale';
import daSmartCharge from './translations/da/SmartCharge';
import daOpladere from './translations/da/Opladere';
import daSolcelleaftale from './translations/da/Solcelleaftale';
import daFAQ from './translations/da/FAQ';
import daKontakt from './translations/da/Kontakt';
import daBlog from './translations/da/Blog';
import daGebyrer from './translations/da/Gebyrer';
import daChargers from './translations/da/chargers';
import daInfoPages from './translations/da/infoPages';
import daLegal from './translations/da/legal';
import daWidgets from './translations/da/widgets';
import daPricePage from './translations/da/pricePage';
import daComparePrices from './translations/da/comparePrices';
import daNicolasKj from './translations/da/nicolasKj';
import daTestFrontpageData from './translations/da/testFrontpage';
import daMarketingHardcoded from './translations/da/marketingHardcoded';
import daPioneers from './translations/da/pioneers';
import daUnderstandBill from './translations/da-understandBill';

// Merge all translation files with flattening for common
const translations = {
  da: {
    ...flattenTranslations(daCommon),
    home: daHome,
    elaftale: daElaftale,
    smartCharge: daSmartCharge,
    opladere: daOpladere,
    solar: daSolcelleaftale,
    testFrontpage: daTestFrontpageData,
    faqPage: daFAQ,
    kontakt: daKontakt,
    blogPage: daBlog,
    gebyrer: daGebyrer,
    chargers: daChargers,
    infoPages: daInfoPages,
    legal: daLegal,
    widgets: daWidgets,
    pricePage: daPricePage,
    comparePrices: daComparePrices,
    nicolasKj: daNicolasKj,
    marketingHardcoded: daMarketingHardcoded,
    pioneers: daPioneers,
    understandBill: daUnderstandBill,
  },

};

// Exposed so a lightweight English-only provider (EnLanguageProvider) can supply
// the SAME context to shared components under the /en section, reusing these exact
// translation tables locked to English.
export { translations };

export function LanguageProvider({ children }) {
  const [lang, setLang] = useState('da'); // Default, will be updated by useEffect
  const [initialized, setInitialized] = useState(false);

  // Initialize language on mount - ALWAYS Danish, English is disabled
  React.useEffect(() => {
    const initLanguage = () => {
      // Always use Danish - English is disabled
      setLang('da');
      
      // Remove any ?lang= parameter from URL if present
      if (typeof window !== 'undefined') {
        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.has('lang')) {
          urlParams.delete('lang');
          const newUrl = window.location.pathname + (urlParams.toString() ? '?' + urlParams.toString() : '');
          window.history.replaceState({}, '', newUrl);
        }
      }
      setInitialized(true);
    };

    initLanguage();
  }, []);

  // Switch language and update URL
  const switchLanguage = useCallback(async (newLang) => {
    if (newLang === 'en' && !translations.en) {
      translations.en = (await import('@/components/translations/englishTranslations')).default;
    }
    setLang(newLang);
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href);
      if (newLang === 'da') {
        url.searchParams.delete('lang');
      } else {
        url.searchParams.set('lang', newLang);
      }
      window.history.replaceState({}, '', url.toString());
    }
  }, []);

  // Legacy function for backwards compatibility
  const switchToDanish = useCallback(() => {
    switchLanguage('da');
  }, [switchLanguage]);

  const t = useMemo(() => {
    const currentTranslations = translations[lang] || translations.da;
    
    return (keyPath, replacements = {}) => {
      // First, try direct lookup (for flattened keys like 'footer.companyName')
      if (keyPath in currentTranslations) {
        let value = currentTranslations[keyPath];
        if (typeof value === 'string') {
          return Object.entries(replacements).reduce(
            (str, [key, val]) => str.replace(`{${key}}`, val),
            value
          );
        }
        return value;
      }
      
      // Second, try nested lookup (for nested objects like home.heroTitle1)
      const keys = keyPath.split('.');
      let value = currentTranslations;
      
      for (const key of keys) {
        if (value && typeof value === 'object' && key in value) {
          value = value[key];
        } else {
          console.warn(`Translation missing: ${keyPath}`);
          return keyPath;
        }
      }
      
      if (typeof value === 'string') {
        return Object.entries(replacements).reduce(
          (str, [key, val]) => str.replace(`{${key}}`, val),
          value
        );
      }
      
      return value;
    };
  }, [lang]);

  const localePath = (path) => path;

  const value = {
    lang,
    t,
    localePath,
    switchToDanish,
    switchLanguage,
    isEnglish: lang === 'en',
    isDanish: lang === 'da',
    initialized,
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export default useLanguage;